// Autoplay for hero videos -- the slider on the home page, and a single hero video
// where there is no slider.
//
// Why this exists rather than just passing autoplay=True to {% video_iframe %}:
//
// 1. oilib cannot start these videos. OiVideoBase seeds `player.is_playing` from the
//    autoplay attribute when the player mounts (oivideo/youtube.js player_ready:
//    `_this.player.is_playing = _this.has_autoplay`). Our hero videos are marked
//    autoplay, so the flag is true from the moment they mount -- whether or not the
//    browser actually started playback. OiYouTube.play() is
//    `if (!this.player.is_playing) playVideo()`, so oi.video.play() is a silent no-op
//    for exactly these videos. Measured on the live page: the flag read true while
//    getCurrentTime() stayed at 0. So we drive the vendor player directly and keep
//    oilib's flag in step afterwards.
//
// 2. The URL's own autoplay=1 only gets one chance, at load. A slide that is not the
//    active one is opacity:0, and the browser suspends media in a slide it is not
//    showing; when that slide becomes active again nothing restarts it. So playback
//    has to be (re)asserted every time the active slide changes.
//
// Slide changes are detected by watching for the .swiper-slide-active class rather
// than through the Swiper instance: the hero Swiper is constructed by an inline script
// on DOMContentLoaded, so its instance may not exist when this module runs, and
// loop/fade cloning means the active element is not reliably the nth slide.

const MUTE_BEFORE_PLAY = true; // Safari (and Chrome) only allow muted autoplay.

/**
 * Resolves the oilib player record for an iframe, if it has mounted yet.
 *
 * @param {HTMLIFrameElement} frame
 * @returns {?object}
 */
function loadedVideo(frame) {
    if (!frame || !frame.id || !window.oi || !window.oi.loaded || !window.oi.loaded.video) {
        return null;
    }
    return window.oi.loaded.video[frame.id] || null;
}

/**
 * Plays or pauses through the vendor API, bypassing oilib's own play()/pause() (see
 * the note at the top of this file). oilib's is_playing flag is updated to match so
 * its bookkeeping -- including the pause-all-others cascade it runs on play -- stays
 * coherent.
 *
 * @param {object} video - an oi.loaded.video record
 * @param {"play"|"pause"} action
 */
function drive(video, action) {
    const player = video && video.player;
    if (!player) return;

    try {
        if (video.vendor === "youtube") {
            if (!player.playVideo) return;
            if (action === "play") {
                if (MUTE_BEFORE_PLAY && player.mute) player.mute();
                player.playVideo();
            } else {
                player.pauseVideo();
            }
        } else if (video.vendor === "vimeo") {
            if (!player.play) return;
            if (MUTE_BEFORE_PLAY && player.setMuted) player.setMuted(true);
            // Vimeo's play()/pause() return promises, and play() rejects outright when
            // the browser refuses playback. Unhandled, that surfaces as a console error.
            const result = action === "play" ? player.play() : player.pause();
            if (result && result.catch) result.catch(() => {});
        } else if (player.play) {
            // Native <video>.
            if (MUTE_BEFORE_PLAY) player.muted = true;
            if (action === "play") {
                const result = player.play();
                if (result && result.catch) result.catch(() => {});
            } else {
                player.pause();
            }
        }
        player.is_playing = action === "play";
    } catch (e) {
        // A player that has been torn down (a removed carousel clone) should not take
        // the rest of the sync with it.
    }
}

// A play() that lands while the vendor player is still settling is accepted and then
// quietly ignored -- Vimeo resolves its play() promise without ever starting, and
// YouTube reports PLAYING for a moment and falls back to paused. Neither reports the
// failure, so the only reliable check is whether the playhead actually moves. Retry a
// couple of times, then give up rather than fight a browser that is refusing.
const PLAY_ATTEMPTS = 3;
const PLAY_VERIFY_MS = 900;

/**
 * Current playhead position, normalised across vendors (Vimeo returns a promise).
 *
 * @param {?object} video - an oi.loaded.video record
 * @returns {Promise<?number>}
 */
function currentTime(video) {
    const player = video && video.player;
    if (!player || !player.getCurrentTime) return Promise.resolve(null);
    try {
        return Promise.resolve(player.getCurrentTime()).catch(() => null);
    } catch (e) {
        return Promise.resolve(null);
    }
}

/**
 * Plays a frame's video, waiting for the player to mount if it has not yet, and
 * re-asserting play if the playhead has not moved.
 *
 * @param {HTMLIFrameElement} frame
 * @param {function(): boolean} stillWanted - false once this frame is no longer the one
 *     that should be playing, so a slide that has moved on is not fought over.
 * @param {number} [attempt]
 */
function play(frame, stillWanted, attempt = 1) {
    const video = loadedVideo(frame);

    if (!video) {
        // Not mounted yet -- normal on first paint, since the iframe still has to load
        // and the vendor API to arrive. oilib dispatches this once the player is ready
        // (both youtube.js and vimeo.js do so in player_ready).
        window.addEventListener(
            "oi.loaded.video." + frame.id,
            () => {
                if (stillWanted()) play(frame, stillWanted, attempt);
            },
            { once: true },
        );
        return;
    }

    drive(video, "play");
    if (attempt >= PLAY_ATTEMPTS) return;

    currentTime(video).then((before) => {
        window.setTimeout(() => {
            if (!stillWanted()) return;
            currentTime(video).then((after) => {
                const moving = before !== null && after !== null && after > before;
                if (!moving) play(frame, stillWanted, attempt + 1);
            });
        }, PLAY_VERIFY_MS);
    });
}

/**
 * Plays the video on the active slide of one slider and pauses every other video in
 * that same slider, so an off-screen slide is not left running.
 *
 * @param {HTMLElement} slider
 */
function syncSlider(slider) {
    const active = slider.querySelector(".swiper-slide-active");
    const activeFrame = active ? active.querySelector(".oi-video iframe") : null;

    slider.querySelectorAll(".oi-video iframe").forEach((frame) => {
        if (frame !== activeFrame) drive(loadedVideo(frame), "pause");
    });

    if (activeFrame) {
        play(activeFrame, () => slider.querySelector(".swiper-slide-active") === active);
    }
}

/**
 * Binds one slider: an initial sync, then a re-sync whenever the active slide changes.
 *
 * @param {HTMLElement} slider
 */
function bindSlider(slider) {
    let lastActiveId = null;
    let queued = false;

    const activeId = () => {
        const active = slider.querySelector(".swiper-slide-active");
        const frame = active ? active.querySelector(".oi-video iframe") : null;
        return frame ? frame.id : null;
    };

    const enforce = () => {
        queued = false;
        syncSlider(slider);
    };

    const queue = () => {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(enforce);
    };

    // Swiper adds .swiper-slide-active on init and moves it on every change. It also
    // rewrites plenty of other classes per transition, so only act on a real change of
    // which video is showing.
    new MutationObserver(() => {
        const id = activeId();
        if (id === lastActiveId) return;
        lastActiveId = id;
        queue();
    }).observe(slider, { subtree: true, attributes: true, attributeFilter: ["class"] });

    // Every hero video also carries autoplay=1 in its own URL, so it starts itself the
    // moment it mounts -- and oilib pauses every OTHER video whenever one starts
    // (oivideo state_change -> oi.video.pause(false, id)). A video that mounts after
    // the initial sync therefore both leaves itself running off-slide and stops the one
    // that should be playing. Re-assert once per mount, which is the only moment a
    // video can start on its own; after they have all mounted nothing else self-starts,
    // so there is no feedback loop to guard against.
    slider.querySelectorAll(".oi-video iframe").forEach((frame) => {
        window.addEventListener("oi.loaded.video." + frame.id, queue, { once: true });
    });

    lastActiveId = activeId();
    queue();
}

/**
 * A hero video with no slider around it: play it while it is on screen, pause it once
 * it has scrolled away so it is not decoding behind the rest of the page.
 *
 * @param {HTMLIFrameElement} frame
 */
function bindStandalone(frame) {
    if (!("IntersectionObserver" in window)) {
        play(frame, () => true);
        return;
    }
    let onScreen = false;
    new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                onScreen = entry.isIntersecting;
                if (onScreen) play(frame, () => onScreen);
                else drive(loadedVideo(frame), "pause");
            });
        },
        { threshold: 0.1 },
    ).observe(frame);
}

function init() {
    const frames = [...document.querySelectorAll(".hero-video-cover .oi-video iframe")];
    if (!frames.length) return;

    const sliders = new Set();
    frames.forEach((frame) => {
        // .swiper is Swiper's own container class; .hero-slider is this theme's hook.
        const slider = frame.closest(".hero-slider, .swiper");
        if (slider) sliders.add(slider);
        else bindStandalone(frame);
    });
    sliders.forEach(bindSlider);
}

// oilib is loaded in the <head>, so window.oi normally exists by now; the guard is for
// the case where it does not, since every play path needs it.
if (window.oi) {
    init();
} else {
    window.addEventListener("oilib_loaded", init, { once: true });
}
